// One achievement track, sized like a game library card. The front shows the
// level being worked toward as a collectible (tilting while hovered), in that
// level's color or the track's accent, with the progress below it; a complete
// track turns neutral. The info button in the collectible's corner flips the
// card to its history, and the X in the same spot flips it back.

import React from 'react';
import { InfoIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/utilities/cn';
import type { AchievementTrackProgress } from '@airhead-games/shared/types';
import { CompletedChip, RewardChip } from './AchievementChips';
import AchievementCollectible from './AchievementCollectible';
import AchievementHistory from './AchievementHistory';
import {
  currentMilestone,
  isTrackComplete,
  trackProgress,
} from './achievementTrack';
import { trackPaletteStyle } from './trackPalette';

/** The bar and count toward the current milestone. */
const TrackProgress: React.FC<{ track: AchievementTrackProgress }> = ({
  track,
}) => {
  const complete = isTrackComplete(track);
  const current = currentMilestone(track);
  return (
    <>
      <div
        className={cn(
          'h-2 overflow-hidden rounded-full',
          complete ? 'bg-muted' : 'bg-(--lvl-200) dark:bg-(--lvl-900)'
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            // A finished track fills with the collectible's foil colors, so
            // a full bar reads as done, not as an empty neutral track.
            complete
              ? 'bg-[linear-gradient(90deg,var(--color-pink-400),var(--color-violet-400),var(--color-sky-400),var(--color-yellow-300))]'
              : 'bg-(--lvl-500)'
          )}
          style={{ width: `${trackProgress(track) * 100}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-xs font-medium">
        <span className="truncate">{current.goal}</span>
        <span className="shrink-0 tabular-nums opacity-80">
          {Math.min(track.value, current.threshold).toLocaleString()} /{' '}
          {current.threshold.toLocaleString()}
        </span>
      </div>
    </>
  );
};

type AchievementTrackCardProps = {
  track: AchievementTrackProgress;
  historyOpen: boolean;
  onToggleHistory: () => void;
};

const AchievementTrackCard: React.FC<AchievementTrackCardProps> = ({
  track,
  historyOpen,
  onToggleHistory,
}) => {
  const complete = isTrackComplete(track);

  const historyButton = (className?: string) => (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={
        historyOpen
          ? `Close ${track.title} history`
          : `Show ${track.title} history`
      }
      onClick={onToggleHistory}
      className={cn(
        // The ghost variant's hover fill is a neutral gray; on a colored
        // card it reads as a smudge, so the icon color alone shows hover.
        'hover:bg-transparent dark:hover:bg-transparent',
        className,
        complete
          ? 'text-muted-foreground hover:text-foreground'
          : 'text-(--lvl-600) hover:text-(--lvl-800) dark:text-(--lvl-200) dark:hover:text-(--lvl-50)'
      )}
    >
      {historyOpen ? (
        <XIcon className="size-5" />
      ) : (
        <InfoIcon className="size-5" />
      )}
    </Button>
  );

  return (
    <Card
      tabIndex={0}
      role="group"
      aria-label={track.title}
      onClick={(event) => {
        // Let nested buttons handle their own clicks.
        if ((event.target as HTMLElement).closest('button')) return;
        onToggleHistory();
      }}
      style={
        complete ? undefined : trackPaletteStyle(track.accent, track.level)
      }
      className="relative w-full cursor-pointer gap-0 overflow-hidden p-0 py-0 shadow-sm ring-1 ring-foreground/10 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
    >
      {/* Sizing only: the same box as a game library card (cover + footer). */}
      <div aria-hidden="true" className="aspect-[16/10] w-full" />
      <div aria-hidden="true" className="h-16 md:h-14" />

      <div className="absolute inset-0 flex flex-col bg-card text-card-foreground">
        {historyOpen ? (
          <div className="flex min-h-0 flex-1 flex-col px-5 pt-7 pb-5">
            {/* Where the collectible's corner button was, so it does not
                jump on flip. */}
            {historyButton('absolute top-6 right-6')}
            <AchievementHistory track={track} />
          </div>
        ) : (
          <>
            {/* The collectible fills the cover; the progress, the footer. */}
            <div className="flex aspect-[16/10] w-full shrink-0 px-4 pt-4 [perspective:900px]">
              <AchievementCollectible
                mode="hover"
                track={track}
                level={track.level}
                complete={complete}
                description={
                  <p className="line-clamp-1">{track.description}</p>
                }
                chip={
                  complete ? (
                    <CompletedChip />
                  ) : (
                    <RewardChip
                      reward={currentMilestone(track).reward}
                      accent={track.accent}
                      level={track.level}
                    />
                  )
                }
                corner={historyButton()}
              />
            </div>
            <div className="flex flex-1 flex-col justify-center px-4">
              <TrackProgress track={track} />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default AchievementTrackCard;
